import React, { useMemo, useCallback } from 'react';
import Button from '../ui/Button';
import { formatRelativeDateTime } from '../../utils/dateUtils';

const InterviewDetailsModal = ({ interview, onClose, onReschedule, onCancel }) => {
  const { statusColor, typeIcon, canJoin, canModify } = useMemo(() => {
    if (!interview) return {};

    const getStatusColor = (status) => {
      switch (status) {
        case 'scheduled':
          return 'bg-blue-100 text-blue-800';
        case 'completed':
          return 'bg-green-100 text-green-800';
        case 'cancelled':
          return 'bg-red-100 text-red-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    const getTypeIcon = (type) => {
      switch (type) {
        case 'video':
          return '📹';
        case 'phone':
          return '📞';
        case 'in-person':
          return '🏢';
        default:
          return '💼';
      }
    };

    const isUpcoming = interview.status === 'scheduled' && interview.scheduledTime > new Date();
    const canJoin = (interview.meetingLink || interview.phoneNumber) && isUpcoming;
    const canModify = interview.status === 'scheduled';

    return {
      statusColor: getStatusColor(interview.status),
      typeIcon: getTypeIcon(interview.type),
      isUpcoming,
      canJoin,
      canModify
    };
  }, [interview]);

  const handleJoinInterview = useCallback(() => {
    if (interview.type === 'video' && interview.meetingLink) {
      window.open(interview.meetingLink, '_blank');
    } else if (interview.type === 'phone' && interview.phoneNumber) {
      window.open(`tel:${interview.phoneNumber}`);
    }
  }, [interview]);

  if (!interview) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Interview Details</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <span className="text-gray-400 text-xl">×</span>
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="flex items-start space-x-4">
            <div className="text-3xl">{typeIcon}</div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{interview.jobTitle}</h3>
              <p className="text-gray-600">
                {interview.companyName && `${interview.companyName} • `}
                {interview.employerName}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                  {interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
                </span>
                <span className="text-sm text-gray-500">
                  Created {interview.createdAt.toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Schedule Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Schedule Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Date & Time:</span>
                <p className="font-medium">{formatRelativeDateTime(interview.scheduledTime)}</p>
              </div>
              <div>
                <span className="text-gray-600">Duration:</span>
                <p className="font-medium">{interview.duration} minutes</p>
              </div>
              <div>
                <span className="text-gray-600">Type:</span>
                <p className="font-medium capitalize">{interview.type}</p>
              </div>
              {interview.type === 'video' && interview.meetingLink && (
                <div>
                  <span className="text-gray-600">Meeting Link:</span>
                  <p className="font-medium text-blue-600 truncate">
                    <a href={interview.meetingLink} target="_blank" rel="noopener noreferrer">
                      {interview.meetingLink}
                    </a>
                  </p>
                </div>
              )}
              {interview.type === 'phone' && interview.phoneNumber && (
                <div>
                  <span className="text-gray-600">Phone Number:</span>
                  <p className="font-medium">
                    <a href={`tel:${interview.phoneNumber}`}>
                      {interview.phoneNumber}
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {interview.notes && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
              <p className="text-gray-700 bg-gray-50 rounded-lg p-3">{interview.notes}</p>
            </div>
          )}

          {/* Feedback */}
          {interview.feedback && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Feedback</h4>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800">{interview.feedback}</p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            {canJoin && (
              <Button onClick={handleJoinInterview}>
                {interview.type === 'video' ? '🎥 Join Meeting' : '📞 Call Now'}
              </Button>
            )}
            
            {canModify && (
              <>
                <Button variant="outline" onClick={() => onReschedule(interview.id)}>
                  📅 Reschedule
                </Button>
                <Button variant="outline" onClick={() => onCancel(interview.id)}>
                  ❌ Cancel Interview
                </Button>
              </>
            )}
            
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewDetailsModal;
